import { resolveEventColor, resolveProviderBadge } from "@/lib/eventColor";
import { Badge } from "@/components/ui/badge";
import { JobStatus } from "../StatusBadge";
import { type AssignedStaffMember } from "@/lib/calendarSpans";

interface QuickPreviewEvent {
  id: string;
  title: string;
  jobNumber?: string;
  customer?: string;
  date: Date;
  endDate?: Date;
  status: JobStatus;
  assignedStaff?: AssignedStaffMember[];
  conflictColor?: string;
  statusColor?: string;
  jobTypeColor?: string;
  providerColor?: string;
  source?: string;
  highestConflictSeverity?: string;
}

interface QuickPreviewPopoverProps {
  event: QuickPreviewEvent;
  anchorEl?: HTMLElement | null;
}

export function QuickPreviewPopover({ event, anchorEl }: QuickPreviewPopoverProps) {
  const color = resolveEventColor(event);
  const badgeColor = resolveProviderBadge(event);

  return (
    <div className="absolute z-50 bg-white shadow-lg rounded-md p-3 border border-[var(--wd-gray-200)] w-48 pointer-events-none">
      <div className="flex justify-between items-start gap-2">
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-sm truncate">{event.title}</h4>
          {event.jobNumber && (
            <div className="text-xs text-muted-foreground">{event.jobNumber}</div>
          )}
        </div>
        
        {/* Provider badge */}
        {event.source && event.source !== 'workdeck' && (
          <div 
            className="h-2 w-2 rounded-full ring-1 ring-background flex-shrink-0"
            style={{ backgroundColor: badgeColor }}
            title={`Source: ${event.source}`}
          />
        )}
      </div>

      {/* Customer */}
      {event.customer && (
        <div className="text-xs text-muted-foreground mt-1">{event.customer}</div>
      )}

      {/* Time range */}
      <div className="text-xs mt-2 font-medium">
        {event.date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        {event.endDate && event.endDate.getTime() !== event.date.getTime() && (
          <>
            {" — "}
            {event.endDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </>
        )}
      </div>

      {/* Assigned staff */}
      {event.assignedStaff && event.assignedStaff.length > 0 && (
        <div className="flex gap-1 flex-wrap mt-2">
          {event.assignedStaff.map((staff) => (
            <Badge 
              key={staff.id} 
              variant="secondary" 
              className="text-[10px] px-1.5 py-0"
            >
              {staff.firstName} {staff.lastName}
            </Badge>
          ))}
        </div>
      )}

      {/* Conflict warning */}
      {event.conflictColor && event.highestConflictSeverity && (
        <div 
          className="mt-2 text-xs font-semibold flex items-center gap-1"
          style={{ color }}
        >
          <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
          Conflict: {event.highestConflictSeverity}
        </div>
      )}

      {/* Status indicator */}
      <div className="mt-2 pt-2 border-t border-border">
        <div className="flex items-center gap-1.5">
          <div 
            className="w-2 h-2 rounded-full" 
            style={{ backgroundColor: color }}
          />
          <span className="text-xs capitalize">{event.status.replace(/-/g, ' ')}</span>
        </div>
      </div>
    </div>
  );
}
